// @ts-nocheck
import React, { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Users, CheckCircle, UserCheck, AlertTriangle, Edit, Trash2 } from "lucide-react";
import { useStaffRoles, useStaffAssignments, useUpcomingEventsForStaffing, useVenues, useEventStaff } from "@/hooks/useStaffingData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { parseLocalDate } from '@/utils/formatters';

const statusColors: Record<string, string> = {
  scheduled: "bg-gray-400 text-white",
  confirmed: "bg-blue-500 text-white",
  checked_in: "bg-green-500 text-white",
  completed: "bg-purple-500 text-white",
  no_show: "bg-red-500 text-white",
  cancelled: "bg-red-400 text-white",
};

const emptyForm = { staff_id: "", venue_id: "", role_id: "", shift_date: "", shift_start: "", shift_end: "", pay_rate: "", pay_type: "", notes: "" };

const AssignmentsTab: React.FC = () => {
  const { data: events, isLoading: evLoading } = useUpcomingEventsForStaffing();
  const { data: roles } = useStaffRoles();
  const { data: staffList } = useEventStaff();
  const queryClient = useQueryClient();

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const { data: assignments, isLoading: assLoading } = useStaffAssignments(selectedEventId);

  const [filterVenue, setFilterVenue] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const selectedEvent = events?.find((e: any) => e.id === selectedEventId);

  const venueIds = useMemo(() => {
    if (!selectedEvent) return [];
    const ids: string[] = [];
    if (selectedEvent.venue_id) ids.push(selectedEvent.venue_id);
    if (selectedEvent.additional_venue_ids && Array.isArray(selectedEvent.additional_venue_ids)) {
      selectedEvent.additional_venue_ids.forEach((vid: string) => { if (!ids.includes(vid)) ids.push(vid); });
    }
    return ids;
  }, [selectedEvent]);

  const { data: venueData } = useVenues(venueIds);
  const getVenueName = (vid: string) => venueData?.find((v: any) => v.id === vid)?.name || selectedEvent?.venue_name || vid;

  const activeStaff = (staffList || []).filter((s: any) => s.status === "active" || !s.status);

  const filtered = (assignments || []).filter((a: any) => {
    if (filterVenue !== "all" && a.venue_id !== filterVenue) return false;
    if (filterRole !== "all" && a.role_id !== filterRole) return false;
    if (filterStatus !== "all" && a.status !== filterStatus) return false;
    return true;
  });

  // Summaries
  const totalAssigned = (assignments || []).length;
  const confirmed = (assignments || []).filter((a: any) => a.status === "confirmed").length;
  const checkedIn = (assignments || []).filter((a: any) => a.status === "checked_in").length;
  const noShows = (assignments || []).filter((a: any) => a.status === "no_show").length;

  const handleRoleSelect = (roleId: string) => {
    const role = roles?.find((r: any) => r.id === roleId);
    setForm({
      ...form,
      role_id: roleId,
      pay_rate: role?.default_pay_rate?.toString() || form.pay_rate,
      pay_type: role?.pay_type || form.pay_type,
    });
  };

  const handleOpen = (assignment?: any) => {
    if (assignment) {
      setEditId(assignment.id);
      setForm({
        staff_id: assignment.staff_id,
        venue_id: assignment.venue_id,
        role_id: assignment.role_id,
        shift_date: assignment.shift_date || "",
        shift_start: assignment.shift_start || "",
        shift_end: assignment.shift_end || "",
        pay_rate: assignment.pay_rate?.toString() || "",
        pay_type: assignment.pay_type || "",
        notes: assignment.notes || "",
      });
    } else {
      setEditId(null);
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.staff_id || !form.venue_id || !form.role_id || !selectedEventId) {
      toast.error("Staff, venue, and role are required");
      return;
    }
    const payload = {
      event_id: selectedEventId,
      staff_id: form.staff_id,
      venue_id: form.venue_id,
      role_id: form.role_id,
      shift_date: form.shift_date || null,
      shift_start: form.shift_start || null,
      shift_end: form.shift_end || null,
      pay_rate: form.pay_rate ? parseFloat(form.pay_rate) : null,
      pay_type: form.pay_type || null,
      notes: form.notes || null,
    };
    if (editId) {
      const { error } = await supabase.from("manage_staff_event_assignments").update({ ...payload, updated_at: new Date().toISOString() }).eq("id", editId);
      if (error) { toast.error("Failed to update"); return; }
      toast.success("Assignment updated");
    } else {
      const { error } = await supabase.from("manage_staff_event_assignments").insert(payload);
      if (error) { toast.error("Failed to create assignment"); return; }
      toast.success("Assignment created");
    }
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ["staff-assignments", selectedEventId] });
  };

  const handleStatusChange = async (id: string, status: string) => {
    const { error } = await supabase.from("manage_staff_event_assignments").update({ status, updated_at: new Date().toISOString() }).eq("id", id);
    if (error) { toast.error("Failed to update status"); return; }
    toast.success(`Status updated to ${status}`);
    queryClient.invalidateQueries({ queryKey: ["staff-assignments", selectedEventId] });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this assignment?")) return;
    const { error } = await supabase.from("manage_staff_event_assignments").delete().eq("id", id);
    if (error) { toast.error("Failed to delete"); return; }
    toast.success("Assignment deleted");
    queryClient.invalidateQueries({ queryKey: ["staff-assignments", selectedEventId] });
  };

  if (evLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="min-w-[280px]">
          <Label>Select Event</Label>
          <Select value={selectedEventId || ""} onValueChange={(v) => setSelectedEventId(v)}>
            <SelectTrigger><SelectValue placeholder="Choose an event..." /></SelectTrigger>
            <SelectContent>
              {(events || []).map((e: any) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name} ({format(parseLocalDate(e.start_date), "MMM d")} - {format(parseLocalDate(e.end_date), "MMM d, yyyy")})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedEventId && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="p-4 flex items-center gap-3">
              <Users className="h-7 w-7 text-primary" />
              <div><p className="text-2xl font-bold">{totalAssigned}</p><p className="text-sm text-muted-foreground">Total Assigned</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <CheckCircle className="h-7 w-7 text-blue-500" />
              <div><p className="text-2xl font-bold">{confirmed}</p><p className="text-sm text-muted-foreground">Confirmed</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <UserCheck className="h-7 w-7 text-green-600" />
              <div><p className="text-2xl font-bold">{checkedIn}</p><p className="text-sm text-muted-foreground">Checked In</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="h-7 w-7 text-destructive" />
              <div><p className="text-2xl font-bold">{noShows}</p><p className="text-sm text-muted-foreground">No Shows</p></div>
            </CardContent></Card>
          </div>

          {/* Filters + Actions */}
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">Venue</Label>
              <Select value={filterVenue} onValueChange={setFilterVenue}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Venues</SelectItem>
                  {venueIds.map(vid => <SelectItem key={vid} value={vid}>{getVenueName(vid)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {(roles || []).map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="checked_in">Checked In</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={() => handleOpen()}><Plus className="h-4 w-4 mr-1" />Add Assignment</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>{editId ? "Edit Assignment" : "Add Assignment"}</DialogTitle></DialogHeader>
                <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                  <div><Label>Staff Member *</Label>
                    <Select value={form.staff_id} onValueChange={(v) => setForm({ ...form, staff_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select staff..." /></SelectTrigger>
                      <SelectContent>
                        {activeStaff.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.name} — {s.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Venue *</Label>
                    <Select value={form.venue_id} onValueChange={(v) => setForm({ ...form, venue_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select venue..." /></SelectTrigger>
                      <SelectContent>
                        {venueIds.map(vid => <SelectItem key={vid} value={vid}>{getVenueName(vid)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Role *</Label>
                    <Select value={form.role_id} onValueChange={handleRoleSelect}>
                      <SelectTrigger><SelectValue placeholder="Select role..." /></SelectTrigger>
                      <SelectContent>{(roles || []).filter((r: any) => r.is_active !== false).map((r: any) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Shift Date</Label><Input type="date" value={form.shift_date} onChange={(e) => setForm({ ...form, shift_date: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Shift Start</Label><Input type="time" value={form.shift_start} onChange={(e) => setForm({ ...form, shift_start: e.target.value })} /></div>
                    <div><Label>Shift End</Label><Input type="time" value={form.shift_end} onChange={(e) => setForm({ ...form, shift_end: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Pay Rate ($)</Label><Input type="number" step="0.01" value={form.pay_rate} onChange={(e) => setForm({ ...form, pay_rate: e.target.value })} /></div>
                    <div><Label>Pay Type</Label>
                      <Select value={form.pay_type} onValueChange={(v) => setForm({ ...form, pay_type: v })}>
                        <SelectTrigger><SelectValue placeholder="Type..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Hourly</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="flat">Flat</SelectItem>
                          <SelectItem value="volunteer">Volunteer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  <Button onClick={handleSave} className="w-full">{editId ? "Update" : "Add"} Assignment</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Assignments Table */}
          {assLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Staff Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Venue</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Shift Date</TableHead>
                        <TableHead>Times</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Pay</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.length === 0 ? (
                        <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No assignments found.</TableCell></TableRow>
                      ) : filtered.map((a: any) => (
                        <TableRow key={a.id}>
                          <TableCell className="font-medium">{a.staff?.name || "—"}</TableCell>
                          <TableCell className="text-sm">{a.staff?.email || "—"}</TableCell>
                          <TableCell className="text-sm">{a.staff?.phone || "—"}</TableCell>
                          <TableCell>{getVenueName(a.venue_id)}</TableCell>
                          <TableCell>{a.role?.name || "—"}</TableCell>
                          <TableCell>{a.shift_date ? format(new Date(a.shift_date), "MMM d") : "—"}</TableCell>
                          <TableCell className="text-sm">{a.shift_start && a.shift_end ? `${a.shift_start}–${a.shift_end}` : "—"}</TableCell>
                          <TableCell>
                            <Select value={a.status} onValueChange={(v) => handleStatusChange(a.id, v)}>
                              <SelectTrigger className="w-[130px]">
                                <Badge className={statusColors[a.status] || ""}>{a.status.replace("_", " ")}</Badge>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="scheduled">Scheduled</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="checked_in">Checked In</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="no_show">No Show</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right text-sm">{a.pay_rate ? `$${a.pay_rate}/${a.pay_type || "—"}` : "—"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" onClick={() => handleOpen(a)}><Edit className="h-4 w-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(a.id)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!selectedEventId && (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Select an event to manage assignments.</CardContent></Card>
      )}
    </div>
  );
};

export default AssignmentsTab;
